/* ============================================================================
   usePolledMessages.ts — hook de polling des messages verifies.

   Contrat :
     - polling toutes les `intervalMs` (default 4000ms)
     - fetch apiClient.getMessages(since) ou since = max(message.createdAt) ou 0
     - append les nouveaux en tete (les recents en premier)
     - pause si document.visibilityState !== 'visible' (economie batterie)
     - resume au focus + reset interval a 4s
     - throttle inverse : si pas de nouveau message depuis 5 polls,
       double l'intervalle (8s, 16s, max 30s) pour economiser.
       Reset a 4s des qu'un nouveau arrive ou au focus.
     - race condition safe : seq counter pour ignorer les responses obsoletes
   ============================================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message, MessagesListResponse } from '@shared/types';
import { apiClient, ApiClientError } from './api';

export interface UsePolledMessagesState {
  messages: Message[];
  loading: boolean;
  error: string | null;
  lastSync: number | null;
  count: number;
  polling: boolean;
}

interface Options {
  intervalMs?: number;
  /** Override pour tests : injecter un fetcher mocke */
  fetcher?: (since?: number) => Promise<MessagesListResponse>;
}

// Polling 5s : compromis ressenti "live" / quota Cloudflare workers.dev
// (~100k req/jour sur free plan). À 2s × 3 onglets, on touchait le rate
// limit en quelques heures. À 5s × 1 onglet = 17k req/jour, bien sous le cap.
// Le backoff exponentiel jusqu'à MAX_INTERVAL après IDLE_BUMPS_BEFORE_BACKOFF
// cycles sans nouveaux messages économise encore plus.
const DEFAULT_INTERVAL = 5000;
const MAX_INTERVAL = 60_000;
const IDLE_BUMPS_BEFORE_BACKOFF = 3;

export function usePolledMessages(options: Options | number = DEFAULT_INTERVAL): UsePolledMessagesState {
  const opts: Options =
    typeof options === 'number' ? { intervalMs: options } : options ?? {};
  const baseIntervalMs = opts.intervalMs ?? DEFAULT_INTERVAL;
  const fetcher = opts.fetcher ?? apiClient.getMessages.bind(apiClient);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [polling, setPolling] = useState<boolean>(
    typeof document === 'undefined' || document.visibilityState === 'visible',
  );

  // refs pour evite les stales closures
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<number>(baseIntervalMs);
  const idleCountRef = useRef<number>(0);
  const messagesRef = useRef<Message[]>([]);
  const stoppedRef = useRef<boolean>(false);

  // sync messagesRef avec state (pour calcul `since`)
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const computeSince = useCallback((): number => {
    const list = messagesRef.current;
    if (list.length === 0) return 0;
    let max = 0;
    for (const m of list) {
      if (m.createdAt > max) max = m.createdAt;
    }
    return max;
  }, []);

  const tick = useCallback(async () => {
    if (stoppedRef.current) return;
    const mySeq = ++seqRef.current;
    const since = computeSince();
    try {
      const res = await fetcher(since || undefined);
      // ignore les responses obsoletes
      if (mySeq !== seqRef.current) return;

      setError(null);
      setLastSync(res.serverTime ?? Date.now());
      setLoading(false);

      const incoming = res.messages ?? [];
      if (incoming.length > 0) {
        // merge : prepend les nouveaux (sort desc par createdAt)
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const fresh = incoming.filter((m) => !existingIds.has(m.id));
          if (fresh.length === 0) return prev;
          const merged = [...fresh, ...prev];
          merged.sort((a, b) => b.createdAt - a.createdAt);
          return merged;
        });
        // reset backoff
        idleCountRef.current = 0;
        intervalRef.current = baseIntervalMs;
      } else {
        idleCountRef.current += 1;
        if (idleCountRef.current >= IDLE_BUMPS_BEFORE_BACKOFF) {
          intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
          idleCountRef.current = 0;
        }
      }
    } catch (err) {
      if (mySeq !== seqRef.current) return;
      const msg =
        err instanceof ApiClientError
          ? `${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : 'connexion API perdue';
      setError(msg);
      setLoading(false);
    }
  }, [baseIntervalMs, computeSince, fetcher]);

  // boucle de polling : utilise setTimeout chaine pour respecter intervalRef dynamique
  useEffect(() => {
    stoppedRef.current = false;

    let cancelled = false;

    const schedule = () => {
      if (cancelled || stoppedRef.current) return;
      timerRef.current = setTimeout(async () => {
        if (cancelled || stoppedRef.current) return;
        await tick();
        schedule();
      }, intervalRef.current);
    };

    // initial fetch immediat
    void tick().then(() => {
      if (!cancelled && !stoppedRef.current) schedule();
    });

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  // Visibility API : pause/reprise + reset interval
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setPolling(visible);
      stoppedRef.current = !visible;
      if (visible) {
        // reset backoff + tick immediat
        intervalRef.current = baseIntervalMs;
        idleCountRef.current = 0;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        void tick();
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [baseIntervalMs, tick]);

  return {
    messages,
    loading,
    error,
    lastSync,
    count: messages.length,
    polling,
  };
}
